/**
 * Shared API client internals: base config, tenant context, fetch/response
 * helpers, and the billing `ApiError` class.
 *
 * These are consumed by every domain module in `app/lib/api/` via relative
 * imports (`./shared`). Only `setActiveTenantId` and `ApiError` are part of
 * the public `@/lib/api` surface — the rest are cross-module internals.
 */

// API Configuration
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";
export const DEFAULT_TIMEOUT = 10000;

// ============================================================================
// Tenant Context (SFWL-002)
// ============================================================================

/**
 * Active white-label tenant id, registered by TenantThemeProvider after
 * subdomain resolution. The Next.js middleware used to inject `x-tenant-id`
 * on proxied requests; in the Vite SPA the API client attaches it directly.
 */
let activeTenantId: string | null = null;

export function setActiveTenantId(tenantId: string | null): void {
  activeTenantId = tenantId;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from filter parameters
 */
export function buildQueryString(params?: Record<string, string | undefined>): string {
  if (!params) return "";

  const validParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== "all")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
    );

  return validParams.length > 0 ? `?${validParams.join("&")}` : "";
}

/**
 * Fetch wrapper with timeout and error handling
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const headers = new Headers(options.headers);
  if (activeTenantId && !headers.has("x-tenant-id")) {
    headers.set("x-tenant-id", activeTenantId);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Handle API response and throw on error
 */
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Response body is not JSON, use default message
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

// ============================================================================
// Billing error type (moved from the invoices section — see SFWL-041)
// ============================================================================

/**
 * Thrown by the billing surface's response handler in place of a bare
 * `Error` — carries `status` so callers can distinguish a 403 (view-only
 * member, fail-closed per the server guard) from any other failure without
 * the message-substring workaround `handleResponse` callers elsewhere in
 * this file use (see AdminStrategyPricing's `CONCURRENT_EDIT_HINT` comment).
 * Scoped to the billing surface only — `handleResponse` itself is unchanged
 * so every other existing call site is unaffected.
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}
